<template>
  <div class="mt-1">
    <b-form>
      <!-- Paths Section -->
      <fieldset class="border rounded p-1 mb-1">
        <legend class="w-auto px-50 font-weight-bold field-legend">Paths</legend>
        <b-row>
          <b-col cols="12" md="6">
            <b-form-group label="Account Number" label-for="accountNumber">
              <b-form-input id="accountNumber" v-model="pathForm.accountNumber" type="text" :disabled="isSaving"
                maxlength="5" />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group label="Comm Type" label-for="communicationType">
              <div class="d-flex align-items-center">
                <b-form-select id="communicationType" v-model="pathForm.communicationType" :options="commTypeOptions"
                  :disabled="isSaving" class="flex-grow-1" />
                <b-button v-if="isCellularType" variant="outline-secondary" size="sm" class="ml-50 text-nowrap"
                  :disabled="isSaving" @click="$emit('activate-cellular')">
                  Activate
                </b-button>
              </div>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group label="Transmit Delay" label-for="transmitDelay">
              <b-form-input id="transmitDelay" v-model="pathForm.transmitDelay" type="number" :disabled="isSaving"
                min="0" />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group label="Path" label-for="path">
              <b-form-select id="path" v-model="pathForm.path" :options="pathOptions" :disabled="isSaving" />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group label="Path Type" label-for="pathType">
              <b-form-select id="pathType" v-model="pathForm.pathType" :options="pathTypeOptions"
                :disabled="isSaving" />
            </b-form-group>
          </b-col>
        </b-row>
      </fieldset>

      <!-- Supervision & Checkin Row -->
      <b-row>
        <b-col cols="12" :md="showCheckin ? '6' : '12'">
          <fieldset class="border rounded p-1 mb-1">
            <legend class="w-auto px-50 font-weight-bold field-legend">Supervision</legend>
            <b-form-group label="Test Report" label-for="testReport">
              <b-form-select id="testReport" v-model="pathForm.testReport" :options="testReportOptions"
                :disabled="isSaving" />
            </b-form-group>

            <template v-if="pathForm.testReport !== 'N'">
              <b-form-group label="Test Frequency" label-for="testFrequency">
                <b-form-input id="testFrequency" v-model="pathForm.testFrequency" type="number" :disabled="isSaving"
                  min="1" />
              </b-form-group>

              <b-form-group label="Frequency Unit" label-for="frequencyUnit">
                <b-form-select id="frequencyUnit" v-model="pathForm.frequencyUnit" :options="frequencyUnitOptions"
                  :disabled="isSaving" />
              </b-form-group>

              <b-form-group label="Test Time" label-for="testTime">
                <flat-pickr v-model="testTimeDisplay" :config="timePickerConfig" :disabled="isSaving"
                  class="form-control" placeholder="HH:MM am/pm" />
              </b-form-group>
            </template>
          </fieldset>
        </b-col>

        <b-col v-if="showCheckin" cols="12" md="6">
          <fieldset class="border rounded p-1 mb-1">
            <legend class="w-auto px-50 font-weight-bold field-legend">Checkin</legend>
            <b-form-group label="Use Checkin" label-for="useCheckIn">
              <b-form-select id="useCheckIn" v-model="pathForm.useCheckIn" :options="useCheckinOptions"
                :disabled="isSaving" />
            </b-form-group>

            <template v-if="pathForm.useCheckIn === 'Y'">
              <b-form-group label="Check-in (Minutes)" label-for="checkInMinutes">
                <b-form-input id="checkInMinutes" v-model="pathForm.checkInMinutes" type="number" :disabled="isSaving"
                  min="0" />
              </b-form-group>

              <b-form-group label="Fail Time (Minutes)" label-for="failTimeMinutes">
                <b-form-input id="failTimeMinutes" v-model="pathForm.failTimeMinutes" type="number" :disabled="isSaving"
                  min="0" />
              </b-form-group>
            </template>
          </fieldset>
        </b-col>
      </b-row>


      <!-- Comm Type Details (Network / Cellular / WiFi) -->
      <fieldset v-if="showCommDetails" class="border rounded p-1 mb-1">
        <legend class="w-auto px-50 font-weight-bold field-legend">Comm Type Details</legend>
        <b-row>
          <b-col v-if="isNetworkType" cols="12">
            <b-form-checkbox id="ipv6" :checked="pathForm.ipv6 === 'Y'" @change="pathForm.ipv6 = $event ? 'Y' : 'N'"
              :disabled="isSaving" class="mb-1">
              IPv6
            </b-form-checkbox>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group label="Receiver IP" label-for="ip">
              <b-form-input id="ip" v-model="pathForm.ip" type="text"
                :disabled="isSaving || (isNetworkType && pathForm.ipv6 === 'Y')" />
            </b-form-group>
          </b-col>

          <b-col v-if="isNetworkType" cols="12" md="6">
            <b-form-group label="Receiver IPv6" label-for="receiverIpv6">
              <b-form-input id="receiverIpv6" v-model="pathForm.receiverIpv6" type="text"
                :disabled="isSaving || pathForm.ipv6 !== 'Y'" />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group label="Receiver Port" label-for="port">
              <b-form-input id="port" v-model="pathForm.port" type="number" :disabled="isSaving" min="0"
                maxlength="5" />
            </b-form-group>
          </b-col>
        </b-row>
      </fieldset>
    </b-form>
  </div>
</template>

<script>
import { computed, ref, watch } from '@vue/composition-api';
import flatPickr from 'vue-flatpickr-component';

import {
  COMM_TYPE_OPTIONS,
  PATH_OPTIONS,
  PATH_TYPE_OPTIONS,
  TEST_REPORT_OPTIONS,
  FREQUENCY_UNIT_OPTIONS,
  USE_CHECKIN_OPTIONS,
  showsCheckin,
  showsDDPhones,
  showsCommTypeDetails,
  isCellular,
  isNetwork,
} from './useCommPathConfig';

export default {
  name: 'PathTab',
  props: {
    pathForm: {
      type: Object,
      required: true,
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
  },
  components: { flatPickr },
  setup(props) {
    const commTypeOptions = COMM_TYPE_OPTIONS;
    const pathOptions = PATH_OPTIONS;
    const pathTypeOptions = PATH_TYPE_OPTIONS;
    const testReportOptions = TEST_REPORT_OPTIONS;
    const frequencyUnitOptions = FREQUENCY_UNIT_OPTIONS;
    const useCheckinOptions = USE_CHECKIN_OPTIONS;

    const showCheckin = computed(() => showsCheckin(props.pathForm.communicationType));
    const showDDPhones = computed(() => showsDDPhones(props.pathForm.communicationType));
    const showCommDetails = computed(() => showsCommTypeDetails(props.pathForm.communicationType));
    const isCellularType = computed(() => isCellular(props.pathForm.communicationType));
    const isNetworkType = computed(() => isNetwork(props.pathForm.communicationType));

    const timePickerConfig = {
      enableTime: true,
      noCalendar: true,
      dateFormat: 'h:i K', 
      time_24hr: false,
    };

    function normalizeFromBackend(val) {
      if (!val) return '';
      const normalized = val
        .replace(/a\.\s*m\./gi, 'am')
        .replace(/p\.\s*m\./gi, 'pm')
        .trim();

      const humanMatch = normalized.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
      if (humanMatch) {
        const h = humanMatch[1];
        const m = humanMatch[2];
        const ap = humanMatch[3].toUpperCase();
        return `${h}:${m} ${ap}`;
      }

      // Format "0200"
      const rawMatch = normalized.match(/^(\d{2})(\d{2})$/);
      if (rawMatch) {
        let h24 = parseInt(rawMatch[1], 10);
        const min = rawMatch[2];
        const ap = h24 < 12 ? 'AM' : 'PM';
        let h12 = h24 % 12;
        if (h12 === 0) h12 = 12;
        return `${h12}:${min} ${ap}`;
      }

      return '';
    }

    const testTimeDisplay = ref(normalizeFromBackend(props.pathForm.testTime));

    if (testTimeDisplay.value) {
      props.pathForm.testTime = testTimeDisplay.value.toLowerCase();
    }

    watch(testTimeDisplay, (val) => {
      props.pathForm.testTime = val ? val.toLowerCase() : '';
    });

    watch(() => props.pathForm.testTime, (val) => {
      const normalized = normalizeFromBackend(val);
      if (normalized !== testTimeDisplay.value) {
        testTimeDisplay.value = normalized;
      }
    });

    return {
      commTypeOptions,
      pathOptions,
      pathTypeOptions,
      testReportOptions,
      frequencyUnitOptions,
      useCheckinOptions,
      showCheckin,
      showDDPhones,
      showCommDetails,
      isCellularType,
      isNetworkType,
      testTimeDisplay,
      timePickerConfig,
    };
  },
};
</script>

<style scoped lang="scss">
.field-legend {
  font-size: 0.85rem;
  margin-bottom: 0;
  line-height: 1;
}
</style>
